import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Check, X, MapPin, Wifi, QrCode } from "lucide-react";
import { resultMeta, fmtDateTime } from "@/pages/hr/attendanceUtils";

export default function ExceptionsPanel() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("pending");
  const [notes, setNotes] = useState({});

  const load = () => {
    const q = status === "all" ? "" : `?status=${status}`;
    api.get(`/hr/attendance-exceptions${q}`).then(r => setRows(r.data)).catch(e => toast.error(formatApiError(e.response?.data?.detail)));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const review = async (id, action) => {
    try { await api.post(`/hr/attendance-exceptions/${id}/review`, { action, admin_notes: notes[id] || "" });
      toast.success(`Exception ${action}d`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const badge = (s) => s === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
    s === "rejected" ? "bg-rose-100 text-rose-700 border-rose-300" : "bg-amber-100 text-amber-800 border-amber-300";

  return (
    <div data-testid="att-exceptions-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-500">Staff-submitted verification exceptions. Approving preserves the original failed GPS/IP evidence.</p>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="att-exc-status-filter" className="h-9 w-40"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="pending">Pending</SelectItem><SelectItem value="approved">Approved</SelectItem><SelectItem value="rejected">Rejected</SelectItem><SelectItem value="all">All</SelectItem></SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        {rows.length === 0 && <div className="rounded-lg border border-slate-200 bg-white px-4 py-6 text-center text-slate-400 text-sm">No exceptions</div>}
        {rows.map(x => (
          <div key={x.id} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`att-exc-${x.id}`}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm font-medium">{x.employee_id} · <span className="font-mono">{x.date}</span></span>
              <Badge variant="outline" className={badge(x.status)}>{x.status}</Badge>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-1.5">
              {x.failed_verifications?.map((v, i) => (
                <span key={i} className="text-xs flex items-center gap-1">
                  <Badge variant="outline" className="text-[10px]">{v.punch === "clock_in" ? "In" : "Out"}</Badge>
                  {v.location_result && v.location_result !== "not_required" && <Badge variant="outline" className={`${resultMeta(v.location_result).cls} text-[10px]`}><MapPin className="w-2.5 h-2.5 mr-0.5" />{resultMeta(v.location_result).label}</Badge>}
                  {v.network_result && v.network_result !== "not_required" && <Badge variant="outline" className={`${resultMeta(v.network_result).cls} text-[10px]`}><Wifi className="w-2.5 h-2.5 mr-0.5" />{resultMeta(v.network_result).label}</Badge>}
                  {v.qr_result && v.qr_result !== "not_required" && <Badge variant="outline" className={`${resultMeta(v.qr_result).cls} text-[10px]`}><QrCode className="w-2.5 h-2.5 mr-0.5" />{resultMeta(v.qr_result).label}</Badge>}
                </span>
              ))}
            </div>
            <p className="text-sm text-slate-700 italic mb-2">"{x.explanation}"</p>
            {x.status === "pending" ? (
              <div className="flex items-center gap-2">
                <Textarea data-testid={`att-exc-notes-${x.id}`} value={notes[x.id] || ""} onChange={e => setNotes({ ...notes, [x.id]: e.target.value })} placeholder="Admin notes (optional)" rows={1} className="flex-1 min-h-9" />
                <Button size="sm" data-testid={`att-exc-approve-${x.id}`} onClick={() => review(x.id, "approve")} className="bg-emerald-600 hover:bg-emerald-700"><Check className="w-4 h-4" /></Button>
                <Button size="sm" variant="outline" data-testid={`att-exc-reject-${x.id}`} onClick={() => review(x.id, "reject")} className="border-rose-300 text-rose-600"><X className="w-4 h-4" /></Button>
              </div>
            ) : (
              <div className="text-xs text-slate-400">Reviewed by {x.reviewed_by || "—"} · {fmtDateTime(x.review_time)}{x.admin_notes ? ` · ${x.admin_notes}` : ""}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
